import React from 'react';
import { VonChatFilters, VonModelType } from '../types';
import { VON_MODEL_CONFIGS } from '../modelConfigs';

interface ModelSelectorProps {
  filters: VonChatFilters;
  onFiltersChange: (filters: Partial<VonChatFilters>) => void;
}

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  filters,
  onFiltersChange
}) => {
  const handleModelSelect = (modelId: VonModelType | undefined) => {
    onFiltersChange({
      selectedModel: modelId
    });
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Fast': return 'bg-green-100 text-green-800 border-green-200';
      case 'Premium': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Research': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCostColor = (costTier: string) => {
    switch (costTier) {
      case '$': return 'text-green-600';
      case '$$': return 'text-blue-600';
      case '$$$': return 'text-purple-600';
      default: return 'text-gray-600';
    }
  };

  // Convert the record to an array
  const VON_MODELS = Object.values(VON_MODEL_CONFIGS);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">AI Model</label>
        {filters.selectedModel && (
          <button
            onClick={() => handleModelSelect(undefined)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Auto-Select
          </button>
        )}
      </div>

      {/* Auto-Select Option */}
      <button
        onClick={() => handleModelSelect(undefined)}
        className={`w-full p-3 text-left rounded-lg border transition-colors ${
          !filters.selectedModel
            ? 'bg-blue-50 border-blue-200 text-blue-700'
            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium">Auto-Select</div>
            <div className="text-xs text-gray-500">
              Automatically choose optimal model based on query
            </div>
          </div>
          <div className="text-xs text-gray-400">🤖</div>
        </div>
      </button>

      {/* Raw Chat Mode Toggle */}
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-lg">⚡</div>
            <div>
              <div className="text-sm font-medium text-gray-900">Raw Chat Mode</div>
              <div className="text-xs text-gray-600">
                Direct AI experience without transcript context
              </div>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={filters.rawChatMode}
              onChange={(e) => onFiltersChange({ rawChatMode: e.target.checked })}
              className="sr-only"
            />
            <div className={`w-11 h-6 rounded-full transition-colors ${
              filters.rawChatMode 
                ? 'bg-amber-500' 
                : 'bg-gray-300'
            }`}>
              <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                filters.rawChatMode 
                  ? 'translate-x-5' 
                  : 'translate-x-0.5'
              } mt-0.5`}></div>
            </div>
          </label>
        </div>
        {filters.rawChatMode && (
          <div className="mt-2 p-2 bg-amber-100 rounded text-xs text-amber-800">
            <strong>Raw Mode Active:</strong> AI will respond without any transcript-related prompts or context. Pure model experience.
          </div>
        )}
      </div>

      {/* Model Groups */}
      <div className="space-y-4">
        {/* Fast Tier */}
        <div>
          <div className="text-xs font-medium text-gray-600 mb-2 flex items-center">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            Fast Tier (A-Series)
          </div>
          <div className="grid grid-cols-1 gap-2">
            {VON_MODELS.filter(model => model.tier === 'Fast').map(model => (
              <button
                key={model.id}
                onClick={() => handleModelSelect(model.id)}
                className={`p-3 text-left rounded-lg border transition-colors ${
                  filters.selectedModel === model.id
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{model.id}</span>
                      <span className={`px-2 py-1 text-xs rounded-full border ${getTierColor(model.tier)}`}>
                        {model.tier}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {model.description}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {model.contextLength} • {model.bestFor.join(', ')}
                    </div>
                  </div>
                  <div className={`text-sm font-medium ${getCostColor(model.costTier)}`}>
                    {model.costTier}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Premium Tier */}
        <div>
          <div className="text-xs font-medium text-gray-600 mb-2 flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            Premium Tier (L-Series)
          </div>
          <div className="grid grid-cols-1 gap-2">
            {VON_MODELS.filter(model => model.tier === 'Premium').map(model => (
              <button
                key={model.id}
                onClick={() => handleModelSelect(model.id)}
                className={`p-3 text-left rounded-lg border transition-colors ${
                  filters.selectedModel === model.id
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{model.id}</span>
                      <span className={`px-2 py-1 text-xs rounded-full border ${getTierColor(model.tier)}`}>
                        {model.tier}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {model.description}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {model.contextLength} • {model.bestFor.join(', ')}
                    </div>
                  </div>
                  <div className={`text-sm font-medium ${getCostColor(model.costTier)}`}>
                    {model.costTier}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Research Tier */}
        <div>
          <div className="text-xs font-medium text-gray-600 mb-2 flex items-center">
            <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
            Research Tier (Z-Series)
          </div>
          <div className="grid grid-cols-1 gap-2">
            {VON_MODELS.filter(model => model.tier === 'Research').map(model => (
              <button
                key={model.id}
                onClick={() => handleModelSelect(model.id)}
                className={`p-3 text-left rounded-lg border transition-colors ${
                  filters.selectedModel === model.id
                    ? 'bg-purple-50 border-purple-200 text-purple-700'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{model.id}</span>
                      <span className={`px-2 py-1 text-xs rounded-full border ${getTierColor(model.tier)}`}>
                        {model.tier}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {model.description}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {model.contextLength} • {model.bestFor.join(', ')}
                    </div>
                  </div>
                  <div className={`text-sm font-medium ${getCostColor(model.costTier)}`}>
                    {model.costTier}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}; 